import { useCallback, useEffect, useState } from 'react';

/**
 * Light or dark, chosen by the person and remembered on their device.
 *
 * LIGHT IS THE DEFAULT AND THE OPERATING SYSTEM DOES NOT OVERRIDE IT. There is
 * deliberately no `prefers-color-scheme` here and none in index.css: an
 * untouched install renders light on a dark-mode machine. A reputation console
 * is opened beside a client on a call as often as it is read alone at night, so
 * the product picks light and the person changes it if they want to.
 *
 * localStorage is the right store for exactly this and almost nothing else: a
 * per-device display preference, not shared state, never read back by the
 * server. It can throw in a private window or with site data blocked, so every
 * access is guarded and the app renders correctly when it fails.
 */
export const THEME_KEY = 'repcheck-theme';
const THEMES = ['light', 'dark'];

export function readStoredTheme() {
	try {
		const stored = window.localStorage.getItem(THEME_KEY);
		return THEMES.includes(stored) ? stored : 'light';
	} catch {
		return 'light';
	}
}

function applyTheme(theme) {
	// Written explicitly for both, not removed for light: index.css defines the
	// light palette on bare :root, so `light` is a no-op that still states the
	// choice, and anything reading the attribute gets an answer either way.
	document.documentElement.setAttribute('data-theme', theme);
}

export function useTheme() {
	const [theme, setThemeState] = useState(readStoredTheme);

	useEffect(() => { applyTheme(theme); }, [theme]);

	const setTheme = useCallback((next) => {
		if (!THEMES.includes(next)) return;
		setThemeState(next);
		try {
			window.localStorage.setItem(THEME_KEY, next);
		} catch {
			// A blocked or full store costs the person persistence, not the app:
			// the theme still applies for this session.
		}
	}, []);

	const toggleTheme = useCallback(() => {
		setTheme(readStoredThemeFrom(document.documentElement) === 'dark' ? 'light' : 'dark');
	}, [setTheme]);

	return { theme, setTheme, toggleTheme };
}

/** The live attribute, so a toggle cannot act on a stale closure. */
function readStoredThemeFrom(el) {
	return el.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}
